import { Button } from '@fluentui/react-components';
import { ChevronLeft, ChevronRight, History, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DraftHistoryItem } from '../api/ragBackend';

const PAGE_SIZE = 3;

export function PreviousDraftsPanel({
  status,
  items,
  onRefresh,
  onRecover,
}: {
  status: 'idle' | 'loading' | 'ready' | 'error';
  items: DraftHistoryItem[];
  onRefresh: () => void;
  onRecover: (runId: string) => void;
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const visibleItems = useMemo(
    () => items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [items, page],
  );

  useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-sf-primary/15 bg-sf-surface">
      <div className="flex items-center justify-between gap-2 border-b border-sf-primary/10 bg-sf-primary-light/55 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <History className="h-4 w-4 shrink-0 text-sf-primary" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-sf-primary">Previous drafts</p>
            <p className="truncate text-[11px] text-sf-gray">Saved for this Outlook message</p>
          </div>
        </div>
        <Button
          appearance="subtle"
          size="small"
          icon={<RotateCcw className="h-3.5 w-3.5" />}
          onClick={onRefresh}
          disabled={status === 'loading'}
          aria-label="Refresh previous drafts"
          title="Refresh previous drafts"
        />
      </div>

      <div className="divide-y divide-sf-primary/10">
        {visibleItems.map((item) => (
          <div key={item.runId} className="flex items-start gap-2 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-sf-dark">
                {item.draftSubject || 'Saved draft'}
              </p>
              <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-sf-gray">
                {item.draftPreview || item.status}
              </p>
              <p className="mt-1 text-[10px] text-sf-gray/70">
                {new Date(item.createdAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {item.citationCount ? ` · ${item.citationCount} sources` : ''}
              </p>
            </div>
            <Button appearance="subtle" size="small" onClick={() => onRecover(item.runId)}>
              Recover
            </Button>
          </div>
        ))}
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-sf-primary/10 px-3 py-2">
          <p className="text-[10px] text-sf-gray">
            {page * PAGE_SIZE + 1}-{Math.min(items.length, (page + 1) * PAGE_SIZE)} of{' '}
            {items.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronLeft className="h-3.5 w-3.5" />}
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              aria-label="Previous previous-drafts page"
            />
            <span className="min-w-10 text-center text-[10px] font-medium text-sf-gray">
              {page + 1}/{pageCount}
            </span>
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronRight className="h-3.5 w-3.5" />}
              disabled={page === pageCount - 1}
              onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
              aria-label="Next previous-drafts page"
            />
          </div>
        </div>
      )}
    </div>
  );
}
